import { useEffect, useMemo } from 'react'

import { getEcho } from '@/shared/realtime/echo'

export interface RealtimeChannel {
  name: string
  private?: boolean
}

interface RealtimeEvent {
  channel: string
  event: string
  payload: unknown
}

type Listener = (event: RealtimeEvent) => void

interface ChannelState {
  listeners: Set<Listener>
  bound: boolean
}

const channelRegistry = new Map<string, ChannelState>()

function channelKey(name: string, isPrivate: boolean): string {
  return `${isPrivate ? 'private' : 'public'}:${name}`
}

export function subscribeToChannel(
  name: string,
  isPrivate: boolean,
  listener: Listener,
): () => void {
  const key = channelKey(name, isPrivate)
  let state = channelRegistry.get(key)

  if (!state) {
    state = { listeners: new Set(), bound: false }
    channelRegistry.set(key, state)
  }

  state.listeners.add(listener)

  const echo = getEcho()
  if (echo && !state.bound) {
    const subscription = isPrivate ? echo.private(name) : echo.channel(name)
    subscription.listenToAll((event: string, payload: unknown) => {
      if (event.startsWith('pusher:')) {
        return
      }

      const current = channelRegistry.get(key)
      if (!current) {
        return
      }

      for (const cb of current.listeners) {
        cb({ channel: name, event, payload })
      }
    })
    state.bound = true
  }

  return () => {
    const current = channelRegistry.get(key)
    if (!current) {
      return
    }

    current.listeners.delete(listener)

    if (current.listeners.size === 0) {
      getEcho()?.leave(name)
      channelRegistry.delete(key)
    }
  }
}

export function resetChannelRegistry(): void {
  channelRegistry.clear()
}

interface UseRealtimeSubscriptionsInput {
  channels: RealtimeChannel[]
  enabled?: boolean
  onEvent: (event: RealtimeEvent) => void
}

export function useRealtimeSubscriptions({
  channels,
  enabled = true,
  onEvent,
}: UseRealtimeSubscriptionsInput): void {
  const normalizedChannels = useMemo(() => {
    const seen = new Set<string>()
    const result: RealtimeChannel[] = []

    for (const channel of channels) {
      const name = channel.name.trim()
      if (!name) {
        continue
      }

      const isPrivate = channel.private !== false
      const key = `${isPrivate ? 'private' : 'public'}:${name}`
      if (seen.has(key)) {
        continue
      }

      seen.add(key)
      result.push({ name, private: isPrivate })
    }

    return result
  }, [channels])

  useEffect(() => {
    if (!enabled || normalizedChannels.length === 0) {
      return
    }

    const unsubscribers = normalizedChannels.map((channel) =>
      subscribeToChannel(channel.name, channel.private !== false, onEvent),
    )

    return () => {
      for (const unsubscribe of unsubscribers) {
        unsubscribe()
      }
    }
  }, [enabled, normalizedChannels, onEvent])
}
